/**
 * usePromoCapture — globale ?promo=CODE detectie (FASE 2).
 *
 * Wandelt door elke route-change, leest `?promo=` query-param, en slaat de code
 * op in localStorage (max 60 dagen) onder key `imperium_promo`. Verwijdert daarna
 * de query-string uit de URL zodat marketing-pages er niet "vuil" uitzien.
 *
 * Gebruik vanuit de checkout: `getPromoCode()` haalt de code op + verloopdatum.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'imperium_promo';
const TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 dagen

export function storePromoCode(code) {
  if (!code) return;
  const clean = String(code).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
  if (clean.length < 4) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code: clean, captured_at: Date.now() })
    );
  } catch {
    /* ignore */
  }
}

export function getPromoCode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.code) return null;
    if (Date.now() - (obj.captured_at || 0) > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return obj.code;
  } catch {
    return null;
  }
}

export function clearPromoCode() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export default function usePromoCapture() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const promo = params.get('promo');
    if (!promo) return;

    storePromoCode(promo);

    // Verwijder ?promo uit URL (mooie URL behouden)
    params.delete('promo');
    const newSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '', hash: location.hash },
      { replace: true }
    );
  }, [location.pathname, location.search, location.hash, navigate]);
}
